// Traps are stacked, so a drawer opened on top of another (e.g. the mini cart
// over the shop-the-look drawer) restores the one beneath it when it closes.
const focusTrapStack = [];
const trapFocusHandlers = {};

function getFocusableElements(container) {
  return [
    ...container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  ];
}

function detachTrapFocusHandlers() {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);
}

function applyTrapFocusHandlers(container) {
  const getFirst = () => getFocusableElements(container)[0];
  const getLast = () => {
    const focusableElements = getFocusableElements(container);
    return focusableElements[focusableElements.length - 1];
  };

  detachTrapFocusHandlers();

  trapFocusHandlers.focusin = (event) => {
    if (!container.contains(event.target)) {
      getFirst().focus();
      return;
    }

    if (
      event.target !== container &&
      event.target !== getLast() &&
      event.target !== getFirst()
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === getLast() && !event.shiftKey) {
      event.preventDefault();
      getFirst().focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === getFirst()) &&
      event.shiftKey
    ) {
      event.preventDefault();
      getLast().focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);
}

window.onKeyUpEscape = (event) => {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
};

/**
 * Whether nothing is trapped above `container`. Layered drawers use this to
 * decide whether an Escape press or an outside click is theirs to act on.
 */
window.isTopFocusTrap = (container) => {
  const top = focusTrapStack[focusTrapStack.length - 1];

  return !top || top.container === container;
};

/**
 * @param {HTMLElement} [elementToFocus] Element to focus once the trap is gone.
 *   Ignored if it has left the DOM, in which case the restored trap's own
 *   element is used.
 * @param {HTMLElement} [container] The trapped container being released.
 *   Defaults to the top of the stack — pass it whenever the release happens
 *   after a close transition, so a trap opened on top in the meantime survives.
 */
window.removeTrapFocus = (elementToFocus = null, container = null) => {
  const index = container
    ? focusTrapStack.findIndex((trap) => trap.container === container)
    : focusTrapStack.length - 1;

  if (index === -1) return;

  const wasTop = index === focusTrapStack.length - 1;

  // Traps nested inside this one (a submenu within the mobile menu) close with
  // it. Traps merely layered over it (the bag over a drawer) outlive it.
  const survivors = container
    ? focusTrapStack
        .slice(index + 1)
        .filter((trap) => !container.contains(trap.container))
    : [];

  focusTrapStack.splice(index, focusTrapStack.length - index, ...survivors);

  // Something is still trapped above this one — leave its handlers and its
  // focus alone.
  if (!wasTop && survivors.length) return;

  const previous = focusTrapStack[focusTrapStack.length - 1];

  if (!previous) {
    detachTrapFocusHandlers();
    if (elementToFocus?.focus) elementToFocus.focus();
    return;
  }

  applyTrapFocusHandlers(previous.container);
  (elementToFocus?.isConnected
    ? elementToFocus
    : previous.elementToFocus
  )?.focus();
};

window.trapFocus = (container, elementToFocus = container) => {
  // One entry per container, so re-trapping an already-trapped container (the
  // mobile menu, when a submenu closes back onto it) moves it to the top
  // rather than leaving a stale duplicate underneath.
  const existing = focusTrapStack.findIndex(
    (trap) => trap.container === container,
  );
  if (existing !== -1) focusTrapStack.splice(existing, 1);

  focusTrapStack.push({ container, elementToFocus });
  applyTrapFocusHandlers(container);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
};

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open"),
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open"),
    );
  });

  if (summary.closest("header-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});
